import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { GlobalMessageService, GlobalMessageType, OccEndpointsService, RoutingService } from '@spartacus/core';
import { CmsComponentData } from '@spartacus/storefront';
import { Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { UigCMSQuestionnaireFormComponent, UigQuestionnaireElementType, UigQuestionnaireHtmlElement, UigQuestionnaireInputElement, UigQuestionnaireOptionElement, UigQuestionnaireSelectElement, UigQuestionnaireTextareaElement } from './uig-questionnaire-form.model';

@Component({
    selector: 'app-uig-questionnaire-form',
    templateUrl: './uig-questionnaire-form.component.html',
    styleUrls: ['./uig-questionnaire-form.component.scss'],
    standalone: false
})
export class UigQuestionnaireFormComponent implements OnInit {

  private static readonly PLACEHOLDER = "$xx$";

  data$: Observable<UigCMSQuestionnaireFormComponent> = this.componentData.data$;

  form = new UntypedFormGroup({
  });
  elements: any[] = [];
  formName: string;

  constructor(
    protected componentData: CmsComponentData<UigCMSQuestionnaireFormComponent>,
    protected globalMessage: GlobalMessageService,
    private httpClient: HttpClient,
    private endpoints: OccEndpointsService,
    private routing: RoutingService
  ) { }

  ngOnInit(): void {
    this.data$.pipe(
      filter((data) => data != null),
      tap((data) => {
        this.formName = data.formUniqueName;
        let elementTypes: UigQuestionnaireElementType[] = [];
        elementTypes.push(new UigQuestionnaireElementType(UigQuestionnaireInputElement.elementName, UigQuestionnaireInputElement.newInstance));
        elementTypes.push(new UigQuestionnaireElementType(UigQuestionnaireTextareaElement.elementName, UigQuestionnaireTextareaElement.newInstance));
        elementTypes.push(new UigQuestionnaireElementType(UigQuestionnaireSelectElement.elementName, UigQuestionnaireSelectElement.newInstance));
        this.elements.push(new UigQuestionnaireHtmlElement(data.content));

        elementTypes.forEach(type => {
          for (let i = 0; i < this.elements.length; i++) {
            if (this.isHtmlElement(this.elements[i])) {
              let newElements = this.handleElements(this.elements[i].content, type);
              this.elements.splice(i, 1, ...newElements);
              i += newElements.length;
            }
          }
        });
      })
    )
      .subscribe();
  }

  private handleElements(content: string, type: UigQuestionnaireElementType): any[] {
    let contentElement = document.createElement('div');
    contentElement.innerHTML = content;

    let splitElements = this.handleElement(contentElement, type);

    let snippets = contentElement.innerHTML.split(UigQuestionnaireFormComponent.PLACEHOLDER);
    for (let i = 0; i < snippets.length; i++) {
      let uigElement = new UigQuestionnaireHtmlElement(snippets[i]);
      splitElements.splice(i * 2, 0, uigElement);
    }
    return splitElements;
  }

  private handleElement(contentElement: HTMLDivElement, type: UigQuestionnaireElementType): any[] {
    let splitElements: any[] = [];
    let elements = contentElement.getElementsByTagName(type.elementName);
    for (var i = 0; i < elements.length; i++) {
      let element = elements[i];
      if (element.hasAttribute("name") && !element.hasAttribute("disabled")) {
        let newElement = this.setupElement(element, type.newInstance());
        splitElements.push(newElement);
        this.handleAddControl(element, newElement);
        i--;
      }
    }
    return splitElements;
  }

  private handleAddControl(element: Element, newElement: any) {
    let validators: ValidatorFn[] = [];
    if (element.hasAttribute("required")) {
      validators.push(Validators.required);
    }

    let name = element.getAttribute("name");
    if (newElement.type === "radio") {
      if (this.form.controls[name]) {
        if (element.hasAttribute("checked")) {
          this.form.controls[name].setValue(newElement.value);
        }
      }
      else {
        this.form.addControl(name, new UntypedFormControl(element.hasAttribute("checked") ? newElement.value : null, validators));
      }
    }
    else if (newElement.type === "checkbox") {
      if (element.hasAttribute("checked")) {
        this.form.addControl(name, new UntypedFormControl(newElement.value));
      }
    }
    else {
      if (this.isSelectElement(newElement)) {
        let result = newElement.options.filter(option => option.selected).map(option => option.value);
        this.form.addControl(name, new UntypedFormControl(result, validators));
      }
      else {
        this.form.addControl(name, new UntypedFormControl(newElement.value, validators));
      }

    }
  }

  private setupElement(sourceElement: Element, targetElement: any): any {
    let attributes = Object.keys(targetElement);
    for (const attribute of attributes) {
      let specialAttributes = ["readonly", "required", "autofocus", "multiple", "checked", "disabled"];
      if (!this.handleAttributesWithoutValues(specialAttributes, attribute, sourceElement, targetElement)) {
        targetElement[attribute] = sourceElement.hasAttribute(attribute) ? sourceElement.getAttribute(attribute) : null;
      }
    }
    if (this.isSelectElement(targetElement)) {
      this.handleOptions(sourceElement, targetElement);
    }
    else if (sourceElement.innerHTML !== '') {
      targetElement.value = sourceElement.innerHTML; // e.g. textarea
    }
    sourceElement.replaceWith(UigQuestionnaireFormComponent.PLACEHOLDER);
    return targetElement;
  }

  private handleOptions(sourceElement: Element, targetElement: any) {
    let contentElement = document.createElement('div');
    contentElement.innerHTML = sourceElement.innerHTML;
    let elements = contentElement.getElementsByTagName(UigQuestionnaireOptionElement.elementName);
    targetElement.options = [];
    for (var i = 0; i < elements.length; i++) {
      let element = elements[i];
      let newElement = new UigQuestionnaireOptionElement();
      let attributes = Object.keys(newElement);
      for (const attribute of attributes) {
        let specialAttributes = ["readonly", "required", "autofocus", "multiple", "selected", "disabled"];
        if (!this.handleAttributesWithoutValues(specialAttributes, attribute, element, newElement)) {
          newElement[attribute] = element.hasAttribute(attribute) ? element.getAttribute(attribute) : null;
        }
      }
      newElement.innerHTML = element.innerHTML;
      targetElement.options.push(newElement);
    }
  }

  private handleAttributesWithoutValues(names: string[], attribute: string, sourceElement: Element, targetElement: any): boolean {
    for (const name of names) {
      if (name === attribute && sourceElement.hasAttribute(name)) {
        targetElement[name] = name;
        return true;
      }
    }
    return false;
  }

  onCheckboxChange(e: any) {
    if (e.target.checked) {
      this.form.addControl(e.target.name, new UntypedFormControl(e.target.value));
    } else {
      this.form.removeControl(e.target.name);
    }
  }

  isInputElement(element: any) {
    return element instanceof UigQuestionnaireInputElement;
  }

  isInputRadioElement(element: any) {
    return this.isInputElement(element) && element.type === 'radio';
  }

  isInputCheckboxElement(element: any) {
    return this.isInputElement(element) && element.type === 'checkbox';
  }

  isOtherInputElement(element: any) {
    return this.isInputElement(element) && !this.isInputRadioElement(element) && !this.isInputCheckboxElement(element);
  }

  isTextareaElement(element: any) {
    return element instanceof UigQuestionnaireTextareaElement;
  }

  isSelectElement(element: any) {
    return element instanceof UigQuestionnaireSelectElement;
  }

  isHtmlElement(element: any) {
    return element instanceof UigQuestionnaireHtmlElement;
  }

  onSubmit() {
    if (this.form.valid) {
      const urlParams = {
        formName: this.formName
      };

      const url = this.endpoints.buildUrl('questionnaireSubmit', {urlParams: urlParams});
      this.httpClient.post(url, JSON.stringify(this.form.value), { observe: 'body', responseType: 'blob' }).pipe(
        tap(
          (data) => {
            window.scroll(0, 0);
            this.globalMessage.add(
              { key: 'uigQuestionnaire.success' },
              GlobalMessageType.MSG_TYPE_INFO,
              10000
            );
            this.routing.go(
              {
                cxRoute: '/'
              }
            );
          },
          () => {
            window.scroll(0, 0);
            this.globalMessage.add(
              { key: 'uigQuestionnaire.error' },
              GlobalMessageType.MSG_TYPE_ERROR
            );
          }
        )
      )
        .subscribe((res) => { });
    }
    else {
      window.scroll(0, 0);
      this.globalMessage.add(
        { key: 'uigQuestionnaire.invalid' },
        GlobalMessageType.MSG_TYPE_ERROR
      );
    }
  }

}
